<template>
  <v-card loading="isLoading">
    <v-card-title class="d-flex justify-space-between">
      <div class="d-flex">
        <h6 class="d-flex">
          <span class="d-flex flex-column">
          <span>{{ $t('sustainability_dashboard_ui.sustainability_score_by_dc') }}</span>
        </span>
        </h6>
      </div>
      <info-modal>
        <v-card-title>
          <h4>{{ $t('sustainability_dashboard_ui.modal_info.distribution_center_summary.title') }}</h4>
        </v-card-title>
        <v-card-text v-html="$t('sustainability_dashboard_ui.modal_info.distribution_center_summary.text')"/>
      </info-modal>
    </v-card-title>
    <v-card-text>
      <div>
        <h4 v-if="isEmpty(filteredScores)" class="text-center">
          {{ $t('no_results') }}
        </h4>
        <div v-else>
          <v-row>
            <v-col xl="6" lg="6" class="dc-summary-label align-bottom">
              {{ $t('sustainability_dashboard_ui.dc') }}
            </v-col>
            <v-col xl="3" lg="3" class="dc-summary-label text-wrap">
              {{ $t('sustainability_dashboard_ui.vehicles_analyzed') }}
            </v-col>
            <v-col xl="3" lg="3" class="dc-summary-label text-wrap">
              {{ $t('sustainability_dashboard_ui.routes_analyzed') }}
            </v-col>
          </v-row>
          <v-row v-for="customer in filteredScoresCapped" :key="customer.id" :class="{'highlighted': customerSelected(customer.id)}">
            <v-col xl="6" lg="6" class="dc-summary-score text-right text-truncate py-0 my-0">
              <router-link :to="`/sustainability/customers/${customer.id}`">{{ customer.name }}</router-link>
            </v-col>
            <!-- <v-col xl="3" lg="3" class="dc-summary-score">
              <span v-html="formatScore(customer.score)"/>
              N/A
            </v-col> -->
            <v-col xl="3" lg="3" class="dc-summary-score py-0 my-0">
              {{ vehicleSummary(customer.id) }}
            </v-col>
            <v-col xl="3" lg="3" class="dc-summary-score py-0 my-0">
              {{ routeSummary(customer.id) }}
            </v-col>
          </v-row>
        </div>
        <div class="d-flex justify-content-center" v-if="filteredScores.length > displayLimit">
          <v-btn size="sm" class="mt-2" variant="link" @click="showAll = !showAll">
            <span v-if="!showAll">
              <v-icon>mdi-chevron-down</v-icon> {{ $t('see_more')}}
              </span>
            <span v-else>
              <v-icon>mdi-chevron-up</v-icon> {{ $t('see_less')}}
              </span>
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import Moment from 'moment-timezone'
import { round, filter, includes, map, isEmpty, sortBy, take } from 'lodash'

export default {
  name: 'DistributionCenterSummary',
  components: {
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    SustainabilityMixin
  ],
  props: {
    aVehicles: null,
    cVehicles: null,
    aRoutes: null,
    cRoutes: null,
    loading: null
  },
  data () {
    return {
      sustainabilityCustomers: null,
      customerScores: null,
      displayLimit: 15,
      showAll: true,
      isLoading: this.loading
    }
  },
  computed: {
    /**
     * Filter to only those customers that have a valid score (and thus have valid sustainability data), as well as
     * capping to the display limit if showAll is false.
     * @returns {string[]}
     */
    filteredScores () {
      return sortBy(
        filter(this.sustainabilityCustomers, x => x.name !== null && x.parent_id !== null),
        x => x.name
      )
    },
    filteredScoresCapped () {
      return this.showAll ? this.filteredScores : take(this.filteredScores, this.displayLimit)
    },
    selectedCustomerIds () {
      const param = this.$route.params.customerIds
      if (param) {
        const split = param.split(',')
        return map(filter(this.customers, x => split.includes(x.id.toString())), x => x.id)
      }
      return []
    }
  },
  methods: {
    vehicleSummary (customer) {
      return `${this.aVehicles[customer] || 0} / ${this.cVehicles[customer] || 0}`
    },
    routeSummary (customer) {
      return `${this.aRoutes[customer] || 0} / ${this.cRoutes[customer] || 0}`
    },
    isEmpty: isEmpty,
    async loadData () {
      this.isLoading = true
      try {
        this.sustainabilityCustomers = await this.getSustainabilityCustomers(this.customers)
        // Set the start and end date depending on whether a custom date range has been selected or not.
        // If not, then the value of timeRange should be a relative date span option, eg '-30 days' or '-1 years' that
        // will be parseable by the API
        /*
        let startDate, endDate
        if (this.sustainabilityParams.timeRange !== 'custom') {
          startDate = this.sustainabilityParams.timeRange
          endDate = 'now'
        } else {
          startDate = Moment.tz(this.sustainabilityParams.startDate, this.customerTimezone).format()
          endDate = Moment.tz(this.sustainabilityParams.endDate, this.customerTimezone).format()
        }
        const customerScoresResult = await this.$httpAdapter.get('/sustainability-insights/sustainability-scores', {
          params: {
            start: startDate,
            end: endDate
          }
        })
        this.customerScores = customerScoresResult.data
        **/
      } catch (error) {
        this.renderHttpAdapterErrorBanner(error)
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Format the score with a percent and round
     * @param score
     * @returns {string}
     */
    formatScore (score) {
      return round(score, 1) + '%'
    },
    /**
     * Return whether the customer id is currently selected.
     * @param customerId
     * @returns {boolean}
     */
    customerSelected (customerId) {
      return includes(this.selectedCustomerIds, customerId)
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>

<style scoped>
.highlighted {
  background-color: rgba(74, 136, 186, .25);
}

.align-bottom {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: right;
}
</style>
